<template>
	<div class="modules">
		<div style="position: sticky; top:12px; z-index: 200;">
			<Breadcrumbs :items="items" :cart="true" />
		</div>
		<v-container fluid>
			<template v-if="this.$store.getters.spaceSubscriptionPlan">
				<v-row no-gutters align="center" class="mt-3 mb-3">
					<v-icon color="primary mr-9" class="mr-4">mdi-format-list-checkbox</v-icon>
					<span class="title font-weight-regular primary--text" v-translate key="current-plan">Current Plan</span>
					<v-tooltip bottom>
						<template v-slot:activator="{ on }">
							<v-btn class="float-right" icon @click="showPlansInfo = true" v-on="on">
								<v-icon color="primary">info_outline</v-icon>
							</v-btn>
						</template>
						<span>Info</span>
					</v-tooltip>
					<v-spacer></v-spacer>
					<v-btn @click="openReferralDialog" color="primary">
						<v-icon class="mr-2">mdi-account-multiple-plus</v-icon>
						<translate>Invite Friends</translate>
					</v-btn>
				</v-row>
				<v-dialog v-model="showPlansInfo" width="40%">
					<v-card>
						<v-card-title class="primary--text mb-2 secondary">
							<translate>Plans Info</translate>
						</v-card-title>
						<v-card-text>
							<v-simple-table>
								<template v-slot:default>
									<thead>
										<tr>
											<th v-translate>N. Max.</th>
											<th v-for="plan in paidSubscriptionPlans" :key="plan.id">{{plan.name}}<span v-if="plan.custom">*</span></th>
										</tr>
									</thead>
									<tbody>
										<tr>
											<td><translate>Patients</translate></td>
											<td v-for="plan in paidSubscriptionPlans" :key="plan.id">
												<span v-if="plan.num_patients == 0" v-translate>Unlimited</span>
												<span v-else>{{plan.num_patients}}</span>
											</td>
										</tr>
										<tr>
											<td><translate>Professionals</translate></td>
											<td v-for="plan in paidSubscriptionPlans" :key="plan.id">
												<span v-if="plan.num_professionals == 0" v-translate>Unlimited</span>
												<span v-else>{{plan.num_professionals}}</span>
											</td>
										</tr>
										<tr>
											<td><translate>Secretaries</translate></td>
											<td v-for="plan in paidSubscriptionPlans" :key="plan.id">
												<span v-if="plan.num_secretaries == 0" v-translate>Unlimited</span>
												<span v-else>{{plan.num_secretaries}}</span>
											</td>
										</tr>
										<tr>
											<td><translate>Disk space</translate></td>
											<td v-for="plan in paidSubscriptionPlans" :key="plan.id">
												<span v-if="plan.disk_space == 0" v-translate>Unlimited</span>
												<span v-else>{{plan.disk_space}}GB</span>
											</td>
										</tr>
									</tbody>
								</template>
							</v-simple-table>
						</v-card-text>
						<v-divider></v-divider>
						<v-card-actions>
							<v-spacer></v-spacer>
							<v-btn color="primary" text @click="showPlansInfo = false">
								<translate>Close</translate>
							</v-btn>
						</v-card-actions>
					</v-card>
				</v-dialog>
				<v-dialog v-model="showReferralDialog" width="40%" scrollable>
					<v-card>
						<v-card-title class="primary--text mb-2 secondary">
							<v-icon color="primary" class="mr-2">mdi-account-multiple-plus</v-icon>
							<translate>Invite Friends</translate>
						</v-card-title>
						<v-card-text>
							<p v-translate>Invite friends and earn extra days in your subscription! You can manually share your referral link or automatically send them an email.</p>
							<v-text-field
								v-model="referralLink"
								class="pl-2 text-caption"
								readonly
								outlined
								dense
								append-icon="mdi-content-copy"
								@click:append="copyLink"
								ref="referralLinkInput"
								hide-details
							/>
							<v-snackbar class="text-caption" :timeout="1000" width="20" absolute right top v-model="linkCopied">
								<translate>Copied</translate>
							</v-snackbar>
							<v-row no-gutters align="center" class="mt-5">
								<v-btn depressed color="secondary" class="primary--text" @click="showSendEmail = !showSendEmail; emailSent = false; sendError = false, emails=['']">
									<translate>Send Email</translate>
									<v-icon v-if="!showSendEmail" class="ml-1">mdi-chevron-down</v-icon>
									<v-icon v-else class="ml-1">mdi-chevron-up</v-icon>
								</v-btn>
								<v-btn v-if="showSendEmail" x-small fab outlined color="primary" class="ml-1" @click="addEmailField">
									<v-icon>mdi-plus</v-icon>
								</v-btn>
								<v-alert v-if="emailSent" dense type="success" class="ma-0 ml-3"><translate>Sent</translate></v-alert>
								<v-alert v-if="sendError" dense type="error" class="ma-0 ml-3"><translate>Error</translate></v-alert>
							</v-row>
							<v-form v-if="showSendEmail" ref="emailForm">
								<v-row v-for="e,i in emails" :key="i" no-gutters class="mt-3" align="center">
									<v-text-field
										v-model="emails[i]"
										class="pl-2 text-caption"
										outlined
										dense
										hide-details
										placeholder="Email"
										:rules="emailRules"
										autofocus
									/>
									<v-btn v-if="i != 0" x-small fab outlined color="primary" class="ml-1" @click="removeEmailField(i)">
										<v-icon>mdi-close</v-icon>
									</v-btn>
								</v-row>
								<v-row no-gutters class="mt-2">
									<v-spacer></v-spacer>
									<v-btn depressed color="secondary" class="ml-2 primary--text" @click="sendEmail" :loading="sendingEmail">
										<translate>Send</translate>
									</v-btn>
								</v-row>
							</v-form>
						</v-card-text>
						<v-divider></v-divider>
						<v-card-actions>
							<v-btn color="primary" text @click="showReferralDialog = false">
								<translate>Close</translate>
							</v-btn>
						</v-card-actions>
					</v-card>
				</v-dialog>
				<v-divider></v-divider>
				<v-row no-gutters class="mt-5">
					<SubscriptionPlanCard :subscriptionPlans="paidSubscriptionPlans" :spaceParams="spaceParams" :currentSubscriptionPlan="currentPlan" />
					<SMSPlan :smsPlans="smsPlans" :notificationsInfo="notificationsInfo" :spaceParams="spaceParams" :currentSubscriptionPlan="currentPlan" />
				</v-row>
			</template>
			<v-row no-gutters class="mt-3 mb-3 align-center">
				<v-icon color="primary mr-9" class="mr-4">add_to_queue</v-icon>
				<span class="title font-weight-regular primary--text" v-translate key="my-modules">Modules</span>
			</v-row>
			<v-divider></v-divider>
			<v-row no-gutters class="mt-5">
				<v-col v-for="m in modules" cols="12" md="4" lg="3" class="px-2" :key="m.id">
					<ModuleCard :module="m" :subscriptionPlans="paidSubscriptionPlans" :spaceParams="spaceParams" :currentPlan="currentPlan" :owned="m.active" />
				</v-col>
			</v-row>
			<v-row
				no-gutters
				class="mt-6 mb-3 align-center"
				@click="showPayments = !showPayments"
				style="cursor: pointer"
			>
				<v-icon color="primary mr-9" class="mr-4">payment</v-icon>
				<span class="title font-weight-regular primary--text" v-translate key="my-payments">My Payments</span>
				<v-icon color="primary mr-9" class="ml-2">{{paymentsIcon}}</v-icon>
			</v-row>
			<template v-if="showPayments">
				<p
					v-if="payments.length == 0"
					class="font-weight-thin ml-2"
					v-translate
					key="no-payment"
				>You have no payment history.</p>
				<v-list class="mt-5">
					<PaymentItem v-for="(payment, index) in payments" :key="index" :payment="payment" />
				</v-list>
			</template>
			<v-dialog v-model="showCCPaymentResult" width="40%" scrollable>
				<v-card>
					<v-card-text class="py-12">
						<v-row no-gutters justify="center" align="center">
							<v-icon v-if="CCPaymentResult == 'ok'" size="70" color="primary">mdi-checkbox-marked-circle</v-icon>
							<v-icon v-else size="70" color="red">mdi-alert-circle</v-icon>
						</v-row>
						<v-row no-gutters justify="center" align="center" class="title">
							<span v-if="CCPaymentResult == 'ok'" v-translate>Payment successful!</span>
							<span v-else v-translate>Payment failed!</span>
						</v-row>
					</v-card-text>
					<v-divider></v-divider>
					<v-card-actions>
						<v-spacer></v-spacer>
						<v-btn color="primary darken-1" text @click="showCCPaymentResult = false">
							<translate>OK</translate>
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-dialog>
		</v-container>
	</div>
</template>

<script>
import ModuleCard from "@/components/modules/ModuleCard.vue";
import SubscriptionPlanCard from "@/components/modules/SubscriptionPlanCard.vue";
import PaymentItem from "@/components/payments/PaymentItem.vue";
import Breadcrumbs from "@/components/Breadcrumbs.vue";
import SMSPlan from "../../components/modules/SMSPlan.vue";
import axios from "axios";

export default {
	name: "Modules",
	props: ["openReferral"],
	components: {
		ModuleCard,
		Breadcrumbs,
		SubscriptionPlanCard,
		PaymentItem,
		SMSPlan
	},
	data: () => ({
		modules: [],
		payments: [],
		subscriptionPlans: [],
		smsPlans: [],
		notificationsInfo: {},
		showPayments: false,
		showPlansInfo: false,
		showCCPaymentResult: false,
		CCPaymentResult: '',
		spaceParams: {},
		showReferralDialog: false,
		linkCopied: false,
		showSendEmail: false,
		emails: [""],
		sendingEmail: false,
		emailSent: false,
		sendError: false
	}),
	computed: {
		items() {
			return [
				{
					text: this.$gettext("Modules"),
					disabled: false,
					link: true,
					to: { name: "modules" }
				}
			];
		},
		paymentsIcon() {
			if (this.showPayments) return "mdi-chevron-up";
			else return "mdi-chevron-down";
		},
		currentPlan() {
			let planInfo = this.$store.getters.spaceSubscriptionPlan;
			if(!planInfo) return null;
			for(let i = 0; i < this.subscriptionPlans.length; i++){
				if(this.subscriptionPlans[i].code == planInfo.plan.code) {
					let plan = this.subscriptionPlans[i];
					plan.days_left = planInfo.days_left;
					plan.percentage = planInfo.percentage;
					return plan
				}
			}
			return planInfo
		},
		paidSubscriptionPlans() {
			return this.subscriptionPlans.filter(sp => sp.code != 'free' && sp.code != 'consultant' && sp.code != 'student');
		},
		referralLink() {
			return (
				"https://app.naltio.com/register?referral=" +
				this.$store.getters.spaceId
			);
		},
		emailRules() {
			return [
				v => !!v || this.$gettext("Email is required"),
				v => /.+@.+\..+/.test(v) || this.$gettext("Email must be valid")
			];
		}
	},
	methods: {
		getStatusColor(status) {
			if (status === "paid") return "primary--text";
			if (status === "pending") return "orange--text";
			else return "red--text";
		},
		getStatus(payment) {
			if (payment.free) return this.$gettext("Free");
			else if (payment.status === "paid") return this.$gettext("Paid");
			else if (payment.status === "pending") return this.$gettext("Pending");
			else return this.$gettext("Failed");
		},
		init() {
			axios
				.get("/modules")
				.then(response => {
					if (response.status === 200) {
						console.log(response.data);
						this.modules = response.data.modules;
						this.payments = response.data.payments;
						this.subscriptionPlans = response.data.subscription_plans;
						this.notificationsInfo = response.data.notifications_info;
						this.smsPlans = response.data.sms_plans;
						this.spaceParams = response.data.space_params;
						this.$store.dispatch("UPDATE_USER", response.data.user);
						let currentPlan = response.data.subscription_plan;
						if(currentPlan && currentPlan.custom) {
							for(let i = 0; i < this.subscriptionPlans.length; i++){
								if(this.subscriptionPlans[i].code == currentPlan.code) {
									this.subscriptionPlans[i].id = currentPlan.id;
									this.subscriptionPlans[i].num_patients = currentPlan.num_patients;
									this.subscriptionPlans[i].num_professionals = currentPlan.num_professionals;
									this.subscriptionPlans[i].num_secretaries = currentPlan.num_secretaries;
									this.subscriptionPlans[i].disk_space = currentPlan.disk_space;
									this.subscriptionPlans[i].custom = true;
								}
							}
						}
						let result = this.$route.query.s;
						if(result) {
							this.CCPaymentResult = result;
							this.showCCPaymentResult = true;
						}
					}
				})
				.catch(error => console.log(error));
		},
		openReferralDialog() {
			this.emails = [""];
			this.showSendEmail = false;
			this.sendingEmail = false;
			this.emailSent = false;
			this.sendError = false;
			this.showReferralDialog = true;
		},
		copyLink() {
			let textToCopy = this.$refs.referralLinkInput.$el.querySelector("input");
			textToCopy.select();
			document.execCommand("copy");
			this.linkCopied = true;
		},
		addEmailField() {
			this.emails.push("");
		},
		removeEmailField(index) {
			this.emails.splice(index, 1);
		},
		sendEmail() {
			if(this.$refs.emailForm.validate()) {
				this.sendingEmail = true;
				this.emailSent = false;
				this.sendError = false;
				axios
					.post("/send-referral-email", {'emails': this.emails, 'link': this.referralLink})
					.then(response => {
						console.log(response.data);
						this.sendingEmail = false;
						this.showSendEmail = false;
						this.emails = [""];
						this.emailSent = true;
					})
					.catch(error => {
						console.log(error);
						this.sendingEmail = false;
						this.sendError = true;
					});
			}
		}
	},
	created() {
		this.init();
		if(this.openReferral) this.showReferralDialog = true;
	}
};
</script>